import React from "react";
import { QuizResult } from "./QuizRun.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

interface QuizSummaryProps {
    result: QuizResult;
    /** Best score for this operation and length, after this run. */
    best: number | null;
    isNewBest: boolean;
    onPlayAgain(): void;
}

/**
 * The end of a run: the score, then every problem that was missed with the
 * answer it wanted - one place to look, once the pressure is off.
 */
export function QuizSummary({ result, best, isNewBest, onPlayAgain }: QuizSummaryProps) {
    const { correct, wrong, skipped, misses } = result;
    const answered = correct + wrong + skipped;
    const t = useTranslation();

    return (
        <div className="quizSummary">
            <p className="quizSummaryScore">{correct}</p>
            <p className="quizSummaryScoreLabel">
                {t('flashCards.correctCaption')}{result.durationMs !== null ? t('flashCards.againstTheClockSuffix') : ""}
            </p>

            <p className="quizSummaryCounts">
                {format(t('flashCards.answeredCountsTemplate'), { answered, wrong, skipped })}
            </p>

            {best !== null && (
                <p className={`quizSummaryBest${isNewBest ? " quizSummaryBest--new" : ""}`}>
                    {isNewBest ? t('flashCards.newBest') : format(t('flashCards.yourBestTemplate'), { n: best })}
                </p>
            )}

            {misses.length > 0 && (
                <div className="quizSummaryMisses">
                    <h3>{t('flashCards.worthAnotherLookHeading')}</h3>
                    <ul>
                        {misses.map((miss, i) => (
                            <li key={i}>
                                <span className="quizSummaryMissProblem">
                                    {miss.front} = {miss.answer}
                                </span>
                                <span className="quizSummaryMissYours">
                                    {miss.skipped ? t('flashCards.skippedLower') : format(t('flashCards.youPutTemplate'), { typed: miss.typed })}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <button type="button" className="quizPlayAgain" onClick={onPlayAgain}>
                {t('flashCards.playAgain')}
            </button>
        </div>
    );
}
